import React from 'react';
import { FlatList } from 'react-native';
import { Paragraph, Surface, Title as PTitle } from 'react-native-paper';
import styled from 'styled-components/native';

import BaseImageBackground from '@src/components/ImageBackground';
import Separator from '@src/components/Separator';
import {
  idKeyExtractor,
  maxWidth,
  padding,
  windowWidth,
} from '@src/configuration/constants';
import { Post } from '@src/types';

import { ItemProps } from './types';

const itemSize = windowWidth / 2 - 2 * padding;

export const List = styled(FlatList as new () => FlatList<Post>).attrs(() => ({
  removeClippedSubviews: true,
  scrollEventThrottle: 8,
  initialNumToRender: 1,
  maxToRenderPerBatch: 1,
  keyExtractor: idKeyExtractor,
  showsHorizontalScrollIndicator: false,
  ItemSeparatorComponent: Separator,
  contentContainerStyle: {
    paddingBottom: padding,
    maxWidth: maxWidth,
    marginHorizontal: 'auto',
  },
}))``;

export const Row = styled.View`
  width: 100%;
  display: flex;
  flex: 1;
  flex-direction: row;
  justify-content: space-evenly;
  align-items: center;
  padding: ${padding}px;
  flex-wrap: wrap;
`;

export const Column = styled.View`
  width: 100%;
  display: flex;
  flex: 1;
  flex-direction: column;
  justify-content: space-evenly;
  align-items: center;
  padding: ${padding}px;
  flex-wrap: nowrap;
`;

const Container = styled(Surface)`
  width: ${itemSize}px;
  height: ${itemSize}px;
  border-radius: ${({ theme }) => theme.roundness}px;
  margin: ${padding / 2}px;
  position: relative;
`;

const ImageBackground = styled(BaseImageBackground).attrs(() => ({
  resizeMode: 'cover',
}))`
  width: ${itemSize}px;
  height: ${itemSize}px;
  border-radius: ${({ theme }) => theme.roundness}px;
  z-index: -10;
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
`;

const Header = styled(PTitle).attrs(({ children }) => ({
  numberOfLines: 1,
  ellipsizeMode: 'tail',
  children:
    (typeof children === 'string' && children.toUpperCase()) || children,
}))`
  font-family: ${({ theme }) => theme.fonts.medium.fontFamily};
  font-weight: 800;
  font-size: ${({ theme }) => theme.typography.heading[1].fontSize}px;
  line-height: ${({ theme }) => theme.typography.heading[1].lineHeight}px;
  color: ${({ theme }) => theme.colors.onPrimary};
  margin-bottom: 20px;
  text-shadow: -1px 1px 2px ${({ theme }) => theme.colors.backdrop};
`;

const Description = styled(Paragraph).attrs(() => ({
  dir: 'unset',
  numberOfLines: 2,
  ellipsizeMode: 'tail',
}))`
  font-family: ${({ theme }) => theme.fonts.regular.fontFamily};
  font-weight: ${({ theme }) => theme.fonts.regular.fontWeight};
  font-size: ${({ theme }) => theme.typography.body[1].fontSize}px;
  line-height: ${({ theme }) => theme.typography.body[1].lineHeight}px;
  color: ${({ theme }) => theme.colors.onPrimary};
  text-shadow: -1px 1px 5px ${({ theme }) => theme.colors.backdrop};
`;

const BigHeader = styled(PTitle)`
  font-family: ${({ theme }) => theme.fonts.medium.fontFamily};
  font-size: ${({ theme }) => theme.typography.body[1].fontSize * 2}px;
  line-height: ${({ theme }) => theme.typography.heading[1].lineHeight * 2}px;
  color: ${({ theme }) => theme.colors.onSurface};
  text-align: center;
  font-weight: 800;
  margin: 10px auto;
`;

const SubHeader = styled(BigHeader)`
  font-size: ${({ theme }) => theme.typography.body[1].fontSize}px;
  line-height: ${({ theme }) => theme.typography.heading[1].lineHeight * 0.8}px;
  padding: 0 ${padding * 4}px;
`;

const Button = styled.TouchableOpacity.attrs(() => ({
  activeOpacity: 0.7,
}))`
  display: flex;
  justify-content: center;
  align-items: center;
`;

export const Item: React.FC<ItemProps> = ({
  image,
  description,
  title,
  onPress,
}) => {
  return (
    <Button onPress={onPress}>
      <Container>
        <ImageBackground source={image} />
        <Column>
          <Header>{title}</Header>
          <Description>{description}</Description>
        </Column>
      </Container>
    </Button>
  );
};

export const Title: React.FC<{}> = ({ children }) => {
  return <BigHeader>{children}</BigHeader>;
};

export const SubTitle: React.FC<{}> = ({ children }) => {
  return <SubHeader>{children}</SubHeader>;
};
